import {Checkbox, Switch, ToggleButtonGroup} from '@wandb/components';
import {type BoundingBox2D} from '@wandb/weave/common/types/media';
import {BoxSliderControls} from '@wandb/weave/components/Panel2/ControlImageOverlays';
import {type ControlsImageOverlaysControls} from '@wandb/weave/components/Panel2/ControlImageOverlaysTypes';
import {
  type BoxControlState,
  type ClassSetControls,
  type MaskControlState,
} from '@wandb/weave/components/Panel2/controlsImage';
import {defaultHideImageState} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import * as Slider from '@wandb/weave/components/Slider';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {debounce, type DebouncedFunc} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import useLatest from 'react-use/lib/useLatest';

type Props = {
  boxes: Record<string, BoundingBox2D[]>;
  controls: ControlsImageOverlaysControls;
  classSets: ClassSetControls;
  updateControls: (partial: Partial<ControlsImageOverlaysControls>) => void;
  scope: React.ReactNode;
  showScope?: boolean;
};

export const ImageControlsScopeContext = React.createContext(true);

type OverlayUpdate = Partial<
  Omit<BoxControlState, 'type'> & Omit<MaskControlState, 'type'>
>;

const MaskOpacityControl = ({
  name,
  opacity,
  disabled,
  onChange,
}: {
  name: string;
  opacity: number;
  disabled: boolean;
  onChange: (opacity: number) => void;
}) => {
  const [draftOpacity, setDraftOpacity] = useState(Math.round(opacity * 100));
  const isDraggingRef = useRef(false);
  const onChangeRef = useLatest(onChange);
  const updateOpacityRef = useRef<DebouncedFunc<
    (value: number) => void
  > | null>(null);
  useEffect(() => {
    // Bound mask pixel processing while preserving previews during long drags.
    const updateOpacity = debounce(
      (value: number) => onChangeRef.current(value / 100),
      50,
      {maxWait: 100}
    );
    updateOpacityRef.current = updateOpacity;
    return () => {
      updateOpacity.flush();
    };
  }, [onChangeRef]);
  useEffect(() => {
    if (!isDraggingRef.current) {
      setDraftOpacity(Math.round(opacity * 100));
    }
  }, [opacity]);
  const finishChange = () => {
    isDraggingRef.current = false;
    updateOpacityRef.current?.flush();
  };

  return (
    <div className="pl-24">
      <div className="flex justify-between text-xs text-wb-text-secondary">
        <span>Opacity</span>
        <span>{draftOpacity}%</span>
      </div>
      <Slider.Root
        min={0}
        max={100}
        step={1}
        value={[draftOpacity]}
        disabled={disabled}
        onValueChange={([value]) => {
          setDraftOpacity(value);
          if (isDraggingRef.current) {
            updateOpacityRef.current?.(value);
          } else {
            onChange(value / 100);
          }
        }}
        onPointerDown={() => {
          isDraggingRef.current = !disabled;
        }}
        onPointerUp={finishChange}
        onPointerCancel={finishChange}>
        <Slider.Track>
          <Slider.Range className="bg-wb-accent" />
        </Slider.Track>
        <Slider.Thumb aria-label={`${name} opacity`} className="h-12 w-12" />
      </Slider.Root>
    </div>
  );
};

export const ImageControls = ({
  boxes,
  controls,
  classSets,
  updateControls,
  scope,
  showScope = true,
}: Props) => {
  const entries = Object.entries(controls.overlayControls ?? {});
  const masks = entries.filter(([, control]) => control.type === 'mask');
  const boxControls = entries.filter(([, control]) => control.type === 'box');
  const layout = controls.tileLayout ?? 'ALL_STACKED';
  const updateOverlay = (id: string, partial: OverlayUpdate) => {
    const control = controls.overlayControls?.[id];
    if (control == null) {
      return;
    }
    updateControls({
      overlayControls: {
        [id]: {...control, ...partial},
      },
    });
  };
  const updateGroup = (group: typeof entries, partial: OverlayUpdate) =>
    updateControls({
      overlayControls: Object.fromEntries(
        group.map(([id, control]) => [id, {...control, ...partial}])
      ),
    });

  return (
    <Tailwind>
      <div className="space-y-16 text-sm text-wb-text-primary">
        <div className="space-y-4">
          <div className="font-semibold">Image controls</div>
          {showScope && (
            <div className="space-y-4 text-xs text-wb-text-secondary">
              {scope}
            </div>
          )}
        </div>
        {masks.length > 0 && (
          <div className="space-y-12">
            <ToggleButtonGroup
              aria-label="Image layout"
              size="sm"
              selectionMode="single"
              disallowEmptySelection
              selectedKeys={[layout]}
              items={[
                {id: 'ALL_STACKED', children: 'Overlay'},
                {id: 'MASKS_NEXT_TO_IMAGE', children: 'Side-by-side'},
                ...(masks.length > 1
                  ? [{id: 'ALL_SPLIT', children: 'Separate masks'}]
                  : []),
              ]}
              onSelectionChange={keys => {
                const selected = [...keys][0];
                if (
                  selected === 'ALL_STACKED' ||
                  selected === 'MASKS_NEXT_TO_IMAGE' ||
                  selected === 'ALL_SPLIT'
                ) {
                  updateControls({tileLayout: selected});
                }
              }}
            />
            <Checkbox
              isSelected={
                !(controls.hideImage ?? defaultHideImageState(layout))
              }
              onChange={show => updateControls({hideImage: !show})}>
              {layout === 'MASKS_NEXT_TO_IMAGE'
                ? 'Show image under overlays'
                : 'Show base image'}
            </Checkbox>
          </div>
        )}
        {(['box', 'mask'] as const).map(type => {
          const group = type === 'box' ? boxControls : masks;
          if (group.length === 0) {
            return null;
          }
          const labelsVisible = group.filter(
            ([, control]) => !control.hideLabels
          ).length;
          return (
            <div
              key={type}
              className="space-y-12 border-t border-wb-border pt-16">
              <Switch
                compact
                isSelected={group.some(([, control]) => !control.disabled)}
                onChange={show => updateGroup(group, {disabled: !show})}>
                {type === 'box'
                  ? 'Show bounding boxes'
                  : 'Show segmentation masks'}
              </Switch>
              {type === 'box' && (
                <Checkbox
                  isSelected={labelsVisible === group.length}
                  isIndeterminate={
                    labelsVisible > 0 && labelsVisible < group.length
                  }
                  onChange={show => updateGroup(group, {hideLabels: !show})}>
                  Show labels
                </Checkbox>
              )}
              {group.map(([id, control]) => (
                <div key={id} className="space-y-12">
                  {group.length > 1 && (
                    <div className="break-words text-xs text-wb-text-secondary">
                      {control.name.replace(/^(box|mask)-/, '')}
                    </div>
                  )}
                  {control.type === 'mask' && layout === 'ALL_SPLIT' && (
                    <Checkbox
                      isSelected={
                        !(control.hideImage ?? defaultHideImageState(layout))
                      }
                      onChange={show => updateOverlay(id, {hideImage: !show})}>
                      Show image under {control.name.replace(/^mask-/, '')}
                    </Checkbox>
                  )}
                  {Object.entries(control.classOverlayStates).map(
                    ([classId, state]) => {
                      const info =
                        classSets[control.classSetID]?.classes[classId];
                      const name = info?.name ?? classId;
                      const updateClass = (partial: Partial<typeof state>) =>
                        updateOverlay(id, {
                          classOverlayStates: {
                            ...control.classOverlayStates,
                            [classId]: {...state, ...partial},
                          },
                        });
                      return (
                        <div key={classId} className="space-y-4">
                          <Checkbox
                            isSelected={!state.disabled}
                            isDisabled={control.disabled}
                            onChange={show => updateClass({disabled: !show})}>
                            <span className="flex items-center gap-8">
                              <span
                                className="h-8 w-8 flex-none rounded-full"
                                style={{backgroundColor: info?.color}}
                              />
                              <span className="break-words">{name}</span>
                            </span>
                          </Checkbox>
                          {type === 'mask' && (
                            <MaskOpacityControl
                              name={name}
                              opacity={state.opacity}
                              disabled={control.disabled || state.disabled}
                              onChange={opacity => updateClass({opacity})}
                            />
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              ))}
              {type === 'box' && (
                <details className="text-xs text-wb-text-secondary">
                  <summary className="cursor-pointer">
                    Advanced box controls
                  </summary>
                  <div className="space-y-12 pt-12">
                    {boxControls.map(([id, control]) =>
                      control.type === 'box' ? (
                        <div key={id} className="space-y-8">
                          <div>
                            {control.name.replace(/^box-/, '')} line style
                          </div>
                          <ToggleButtonGroup
                            aria-label={`${control.name} line style`}
                            size="sm"
                            selectionMode="single"
                            disallowEmptySelection
                            selectedKeys={[control.lineStyle ?? 'line']}
                            items={[
                              {id: 'line', children: 'Solid'},
                              {id: 'dotted', children: 'Dotted'},
                              {id: 'dashed', children: 'Dashed'},
                            ]}
                            onSelectionChange={keys => {
                              const selected = [...keys][0];
                              if (
                                selected === 'line' ||
                                selected === 'dotted' ||
                                selected === 'dashed'
                              ) {
                                updateOverlay(id, {lineStyle: selected});
                              }
                            }}
                          />
                        </div>
                      ) : null
                    )}
                    <div className="overflow-x-auto">
                      <BoxSliderControls
                        boxes={boxes}
                        sliders={controls.boxSliders}
                        updateSliders={sliders =>
                          updateControls({
                            boxSliders: {...controls.boxSliders, ...sliders},
                          })
                        }
                      />
                    </div>
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </Tailwind>
  );
};
